// KPI grid saf mantığı (M16 T10, Eng 17): kolon modeli (yıllar / işletmeler), satır düzleştirme
// (kategori bantları + accordion), klavye gezinme reducer'ı (D7), giriş normalizasyonu
// (parseCell köprüsü), lokal yeniden hesap etki kümesi (kural 5), değişim kolonu.
// Bileşen (KpiGrid) ince kalır; burası vitest ile kilitli.

import {
  changePercent,
  fiscalPeriodRange,
  parseCell,
  shortDateRange,
  type KpiResolvedCell,
  KPI_TOTAL_KIND_LABELS,
  type KpiResolvedGrid,
  type KpiResolvedIndicator,
  type KpiTotalKind,
  type KpiValueStatus,
  type ParsedCell,
} from "@mahir/shared";
import {
  attachTreeAria,
  groupRowInfo,
  memberRowInfo,
  rowKindOf,
  totalRowInfo,
  type GridRowKind,
  type GroupRowInfo,
  type MemberRowInfo,
  type TotalRowInfo,
  type TreeAria,
} from "./kpi-grid-breakdown";
import { yearHeaderLabel } from "./kpi-ui";

// Satır sanallaştırma + sütun genişliği planı: kpi-grid-virtual.ts (salt taşıma, ≤600 satır).
export * from "./kpi-grid-virtual";
// Kırılım satır türleri (tür, hesap grubu, üye yolu, toplam hücresi durumu): kpi-grid-breakdown.ts.
export * from "./kpi-grid-breakdown";

// ─── Kolon modeli ─────────────────────────────────────────────────────────────

export type GridViewMode = "years" | "entities";

export interface GridColumn {
  key: string;
  label: string;
  kind: "year" | "entity" | "consolidated" | "subtotal";
  year: number;
  entityId: string | null;
  /** konsolide / ara toplam kolonları ayrık zeminle çizilir */
  aggregate: boolean;
  /** M17 T5 — yalnız mali yıl yıl kolonunda: "1 Tem 2025 – 30 Haz 2026" (th title) */
  title?: string;
}

/** Toplam sütunu türü (Y6): kapsamlı çözümde `entityScope.totalKind`; yoksa tam kapsam → konsolide. */
export function totalKindOf(grid: Pick<KpiResolvedGrid, "entityScope">): KpiTotalKind {
  return grid.entityScope?.totalKind ?? "consolidated";
}

/** Yıl başına toplam sütun grubuna ait kolon (portal, §12/1). */
export function isTotalColumn(col: GridColumn): boolean {
  return col.key.startsWith("t:");
}

export interface BuildColumnsOpts {
  /**
   * Yalnız portal (`clientView`): "Yıllar" görünümünde seçili yaprağın yıl kolonlarının SAĞINA yıl
   * başına salt-okunur toplam sütunu. Yok/false → çıktı eskisiyle byte-eşit (danışman).
   */
  totalColumns?: boolean;
}

/**
 * Kural 4: tek işletme → yıl kolonları (son yıl solda, "cur"); çok işletme + "entities" modu →
 * aktif yaprak işletme kolonları (seçili yıl), ara toplam (grup) ve en sağda konsolide.
 */
export function buildColumns(
  grid: KpiResolvedGrid,
  mode: GridViewMode,
  entityYear: number,
  /** K27 — çok işletmeli müşteride "Yıllar" görünümünün işletmesi; null/eşleşmeyen → konsolide (salt okunur). */
  yearsEntityId: string | null = null,
  opts?: BuildColumnsOpts,
): GridColumn[] {
  const yearsDesc = [...grid.years].sort((a, b) => b - a);
  const leaves = grid.entities.filter(
    (e) => e.isActive && !grid.entities.some((c) => c.parentId === e.id && c.isActive),
  );
  if (mode === "years" || leaves.length <= 1) {
    const chosen = leaves.length > 1 ? leaves.find((l) => l.id === yearsEntityId) : leaves[0];
    const entityId = chosen?.id ?? leaves[0]?.id ?? null;
    // çok işletmede işletme seçilmediyse "Yıllar" görünümü konsolide satırdan okunur (salt okunur)
    const aggregate = leaves.length > 1 && !chosen;
    const startMonth = grid.fiscalYearStartMonth ?? 1;
    const yearCols = yearsDesc.map((y) => {
      const col: GridColumn = {
        key: `y:${y}`,
        label: yearHeaderLabel(y, startMonth),
        kind: "year",
        year: y,
        entityId,
        aggregate,
      };
      if (startMonth === 1) return col;
      const r = fiscalPeriodRange(y, startMonth, "annual");
      return { ...col, title: shortDateRange(r.start, r.end) };
    });
    // §12/1, §12/3 — seçili yaprak varken yıl başına toplam sütunu (tam kapsam: Konsolide, kapsamlı
    // ≥2 işletme: Kapsamınızdaki işletmeler toplamı; tek işletme/`none` → grup yok)
    const kind = totalKindOf(grid);
    if (!opts?.totalColumns || leaves.length <= 1 || aggregate || kind === "none") return yearCols;
    const label = KPI_TOTAL_KIND_LABELS[kind];
    const totals: GridColumn[] = yearsDesc.map((y) => ({
      key: `t:${y}`,
      label: `${label} · ${yearHeaderLabel(y, startMonth)}`,
      kind: "consolidated",
      year: y,
      entityId: null,
      aggregate: true,
    }));
    return [...yearCols, ...totals];
  }
  const cols: GridColumn[] = [];
  const groups = grid.entities.filter((e) => e.isActive && leaves.every((l) => l.id !== e.id));
  const ordered = [...grid.entities].sort((a, b) => a.orderIndex - b.orderIndex);
  for (const g of groups.length > 0
    ? ordered.filter((e) => groups.some((x) => x.id === e.id))
    : [null]) {
    const children = g ? leaves.filter((l) => l.parentId === g.id) : leaves;
    for (const l of children) {
      cols.push({
        key: `e:${l.id}`,
        label: l.name,
        kind: "entity",
        year: entityYear,
        entityId: l.id,
        aggregate: false,
      });
    }
    if (g && children.length > 0) {
      cols.push({
        key: `s:${g.id}`,
        label: `${g.name} toplam`,
        kind: "subtotal",
        year: entityYear,
        entityId: g.id,
        aggregate: true,
      });
    }
  }
  // grupsuz yapraklar (kökte)
  if (groups.length > 0) {
    for (const l of leaves.filter((x) => !groups.some((g) => g.id === x.parentId))) {
      cols.push({
        key: `e:${l.id}`,
        label: l.name,
        kind: "entity",
        year: entityYear,
        entityId: l.id,
        aggregate: false,
      });
    }
  }
  cols.push({
    key: "c",
    label: "Konsolide",
    kind: "consolidated",
    year: entityYear,
    entityId: null,
    aggregate: true,
  });
  return cols;
}

export function readCell(ind: KpiResolvedIndicator, col: GridColumn): KpiResolvedCell | undefined {
  switch (col.kind) {
    case "year":
      return col.aggregate
        ? ind.consolidated?.[col.year]
        : col.entityId
          ? ind.cells[col.entityId]?.[col.year]
          : undefined;
    case "entity":
      return col.entityId ? ind.cells[col.entityId]?.[col.year] : undefined;
    case "subtotal":
      return col.entityId ? ind.subtotals?.[col.entityId]?.[col.year] : undefined;
    case "consolidated":
      return ind.consolidated?.[col.year];
  }
}

// ─── Satır modeli (bantlar + accordion + kırılım) ───────────────────────────

export interface GridRow {
  indicator: KpiResolvedIndicator;
  /** dört tür (spec §6.1): band kategori, total kırılımlı gösterge, group grup bandı, row yaprak/üye */
  kind: GridRowKind;
  /** hücresiz satır (band | group): odaklanmaz, ok tuşlarında ve yapıştırmada atlanır */
  isBand: boolean;
  depth: number;
  /** bant için: alt ağaçtaki yaprak sayısı + eksik/XXX/hesaplanamadı sayıları */
  bandStats?: { leaves: number; missing: number; pending: number; calcError: number };
  /** band | total: açık mı (`aria-expanded`); varsayılan açık, anahtar `indicator.id` */
  open?: boolean;
  /** yalnız total */
  total?: TotalRowInfo;
  /** yalnız group */
  group?: GroupRowInfo;
  /** yalnız grup altındaki üye (seviye-1 üye-toplam dahil) */
  member?: MemberRowInfo;
  /** treegrid öznitelikleri (`buildRows` sonunda, görünür kardeşlere göre) */
  tree?: TreeAria;
}

export function isBandIndicator(ind: KpiResolvedIndicator, all: KpiResolvedIndicator[]): boolean {
  return all.some((i) => i.parentId === ind.id);
}

/**
 * Bant/toplam kapalıysa (collapsed) alt ağacı gizlenir; arama/filtre eşleşen kategorileri ve
 * toplamları otomatik açar (D6). Grup bandı da aç/kapanır (TODO-KPI-124; anahtar `indicator.id`).
 */
export function buildRows(args: {
  grid: KpiResolvedGrid;
  collapsed: Set<string>;
  search: string;
  categoryId: string | null;
  columns: GridColumn[];
}): GridRow[] {
  const { grid, collapsed, search, categoryId, columns } = args;
  const all = grid.indicators;
  const byId = new Map(all.map((i) => [i.id, i]));
  const byParent = new Map<string | null, KpiResolvedIndicator[]>();
  for (const i of all) {
    const p = i.parentId && byId.has(i.parentId) ? i.parentId : null;
    const arr = byParent.get(p) ?? [];
    arr.push(i);
    byParent.set(p, arr);
  }
  const childrenOf = (id: string): KpiResolvedIndicator[] => byParent.get(id) ?? [];
  const kindOf = (i: KpiResolvedIndicator): GridRowKind => rowKindOf(i, childrenOf(i.id));
  const needle = search.trim().toLocaleLowerCase("tr");
  const matches = (i: KpiResolvedIndicator): boolean =>
    !needle || i.label.toLocaleLowerCase("tr").includes(needle) || i.code.includes(needle);

  // üye bilgisi: grup altındaki düğüm (seviye-1 üye-toplam dahil); yol köke kadar uzar
  const memberOf = (
    ind: KpiResolvedIndicator,
    up: KpiResolvedIndicator | undefined,
  ): MemberRowInfo | undefined => {
    if (!up || kindOf(up) !== "group") return undefined;
    const t = up.parentId ? byId.get(up.parentId) : undefined;
    const total = t && kindOf(t) === "total" ? t : undefined;
    const above = total
      ? memberOf(total, total.parentId ? byId.get(total.parentId) : undefined)
      : undefined;
    return memberRowInfo(ind, up, total, above?.path);
  };

  const out: GridRow[] = [];
  const visit = (parent: string | null, depth: number, forceOpen: boolean): boolean => {
    let anyVisible = false;
    for (const ind of byParent.get(parent) ?? []) {
      const kind = kindOf(ind);
      if (categoryId && depth === 0 && ind.id !== categoryId) continue;
      const up = ind.parentId ? byId.get(ind.parentId) : undefined;
      const member = memberOf(ind, up);
      if (kind === "row") {
        if (needle && !matches(ind)) continue;
        out.push({ indicator: ind, kind, isBand: false, depth, ...(member ? { member } : {}) });
        anyVisible = true;
        continue;
      }
      // band / total / group: çocuklu düğüm (Y4a yapı satırı da bant — görünür torunu var)
      const start = out.length;
      const open = forceOpen || !!needle || !collapsed.has(ind.id);
      const row: GridRow = {
        indicator: ind,
        kind,
        isBand: kind !== "total",
        depth,
        ...(member ? { member } : {}),
      };
      if (kind === "band") {
        row.open = open;
        // yapı satırı değer taşımaz → sayaç da yok (müşteri kapalı kategori hakkında sayı görmez)
        if (!ind.structural) row.bandStats = bandStats(ind, childrenOf, kindOf, columns);
      } else if (kind === "total") {
        row.open = open;
        row.total = totalRowInfo(ind, childrenOf);
      } else {
        row.open = open;
        row.group = groupRowInfo(ind, up, childrenOf);
      }
      out.push(row);
      const childVisible = visit(ind.id, depth + 1, forceOpen);
      if (needle && !childVisible && !matches(ind)) {
        out.splice(start); // eşleşme yok → satırı da düşür
        continue;
      }
      if (!open && !needle) out.splice(start + 1); // kapalı bant/toplam: çocukları gizle
      anyVisible = true;
    }
    return anyVisible;
  };
  visit(null, 0, false);
  attachTreeAria(out, byId);
  return out;
}

/** Sayaç: yaprak/üye hücreleri + ham (türetilmemiş) toplam; grup bandı ve türetilmiş toplam sayılmaz. */
function bandStats(
  band: KpiResolvedIndicator,
  childrenOf: (id: string) => KpiResolvedIndicator[],
  kindOf: (i: KpiResolvedIndicator) => GridRowKind,
  columns: GridColumn[],
): NonNullable<GridRow["bandStats"]> {
  const stats = { leaves: 0, missing: 0, pending: 0, calcError: 0 };
  const stack = [...childrenOf(band.id)];
  while (stack.length) {
    const i = stack.pop()!;
    const kind = kindOf(i);
    stack.push(...childrenOf(i.id));
    if (kind === "band" || kind === "group" || (kind === "total" && i.isDerived)) continue;
    stats.leaves += 1;
    for (const col of columns) {
      const c = readCell(i, col);
      if (!c || c.status === "empty") stats.missing += 1;
      else if (c.status === "pending_xxx") stats.pending += 1;
      else if (c.status === "calc_error") stats.calcError += 1;
    }
  }
  return stats;
}

// ─── Klavye gezinme (D7) ──────────────────────────────────────────────────────

export interface CellPos {
  row: number; // GridRow index (bant satırları atlanır)
  col: number; // GridColumn index
}

export type GridKey =
  | "ArrowUp"
  | "ArrowDown"
  | "ArrowLeft"
  | "ArrowRight"
  | "Tab"
  | "ShiftTab"
  | "Enter"
  | "Escape"
  | "Home"
  | "End";

export interface NavState {
  focus: CellPos | null;
  editing: boolean;
}

/** Düzenlenebilir hücre: bant/grup değil, türetilmiş (ya da hesap gruplu toplam) değil, kolon aggregate değil. */
export function isEditable(rows: GridRow[], columns: GridColumn[], pos: CellPos): boolean {
  const r = rows[pos.row];
  const c = columns[pos.col];
  return (
    !!r && !!c && !r.isBand && !r.indicator.isDerived && r.total?.derived !== true && !c.aggregate
  );
}

/**
 * DR9 — toplam satırında aç/kapa okları (WAI-ARIA treegrid kalıbı): YALNIZ ilk
 * değer kolonunda — açıkken ← kapatır, kapalıyken → açar; diğer kolonlarda oklar gezinir. Enter
 * aç/kapamaz (bilinçli sapma, fix round 1): değer hücresinde önceki davranış (türetilmiş → formül,
 * ham → editör); aç/kapa ad hücresindeki `<button aria-expanded>` ile (Tab, yerel Enter/Space).
 * Döner: aç/kapa edilecek `indicator.id` (odak toplam satırında kalır) ya da null.
 */
export function totalToggleFor(state: NavState, key: GridKey, rows: GridRow[]): string | null {
  const f = state.focus;
  const r = f ? rows[f.row] : undefined;
  if (state.editing || !f || r?.kind !== "total" || f.col !== 0) return null;
  if (key === "ArrowLeft" && r.open) return r.indicator.id;
  if (key === "ArrowRight" && !r.open) return r.indicator.id;
  return null;
}

/**
 * Izgara klavye işleyicisi yalnız değer hücresinden (`role=gridcell`) gelen tuşlara uyar; ad
 * hücresindeki düğmelerden (toplam ▾/▸, kategori bandı) kabarcıklanan Enter/Space düğmenin yerel
 * tıklamasına bırakılır — aksi hâlde `preventDefault` tıklamayı iptal edip `nav.focus`'un (başka
 * olabilecek) satırına etki ederdi (fix round 1, I1).
 */
export function isGridCellKeyTarget(target: EventTarget | null): boolean {
  const el = target as { closest?: (sel: string) => unknown } | null;
  return typeof el?.closest === "function" && el.closest('[role="gridcell"]') !== null;
}

function nextDataRow(rows: GridRow[], from: number, dir: 1 | -1): number {
  let i = from + dir;
  while (i >= 0 && i < rows.length) {
    if (!rows[i]!.isBand) return i;
    i += dir;
  }
  return from;
}

export function navReducer(
  state: NavState,
  key: GridKey,
  rows: GridRow[],
  columns: GridColumn[],
): NavState {
  const f = state.focus;
  if (!f) return state;
  const clamp = (p: CellPos): CellPos => ({
    row: Math.min(Math.max(p.row, 0), rows.length - 1),
    col: Math.min(Math.max(p.col, 0), columns.length - 1),
  });
  if (state.editing) {
    if (key === "Escape") return { focus: f, editing: false };
    if (key === "Enter")
      return { focus: clamp({ row: nextDataRow(rows, f.row, 1), col: f.col }), editing: false };
    if (key === "Tab") return { focus: clamp({ row: f.row, col: f.col + 1 }), editing: false };
    if (key === "ShiftTab") return { focus: clamp({ row: f.row, col: f.col - 1 }), editing: false };
    return state; // ok tuşları düzenleme içinde imleç hareketi
  }
  switch (key) {
    case "ArrowUp":
      return { focus: clamp({ row: nextDataRow(rows, f.row, -1), col: f.col }), editing: false };
    case "ArrowDown":
      return { focus: clamp({ row: nextDataRow(rows, f.row, 1), col: f.col }), editing: false };
    case "ArrowLeft":
    case "ShiftTab":
      return { focus: clamp({ row: f.row, col: f.col - 1 }), editing: false };
    case "ArrowRight":
    case "Tab":
      return { focus: clamp({ row: f.row, col: f.col + 1 }), editing: false };
    case "Home":
      return { focus: clamp({ row: f.row, col: 0 }), editing: false };
    case "End":
      return { focus: clamp({ row: f.row, col: columns.length - 1 }), editing: false };
    case "Enter":
      return { focus: f, editing: isEditable(rows, columns, f) };
    case "Escape":
      return { focus: f, editing: false };
  }
}

// ─── Giriş normalizasyonu (kural 1) ──────────────────────────────────────────

export interface CellInput {
  status: KpiValueStatus;
  numericValue?: number;
  textValue?: string;
}

/**
 * Hücre editörü ham metnini kaydetme argümanına çevirir. Sayı → filled; "XXX"/"XX" → pending_xxx;
 * boş → empty; "-" → empty; metin (ör. "Ölçülmüyor", "2 yılda bir") → not_measured + text.
 * Yüzde göstergede "%41,3" → 41.3. Birim soneki uyuşmazsa `unitWarning` döner (kaydetmeyi engellemez).
 */
export function normalizeCellInput(
  raw: string,
  opts: { unit?: string | null; valueType?: "number" | "percent" | "text" } = {},
): { input: CellInput; parsed: ParsedCell; unitWarning?: string; invalid?: boolean } {
  const parsed = parseCell(raw);
  if (opts.valueType === "text") {
    const t = raw.trim();
    return { input: t ? { status: "filled", textValue: t } : { status: "empty" }, parsed };
  }
  switch (parsed.kind) {
    case "number":
      return {
        input: { status: "filled", numericValue: parsed.value },
        parsed,
        ...(parsed.unit && opts.unit && norm(parsed.unit) !== norm(opts.unit)
          ? { unitWarning: `Birim uyuşmuyor: ${parsed.unit} (gösterge: ${opts.unit})` }
          : {}),
      };
    case "percent":
      return { input: { status: "filled", numericValue: parsed.value }, parsed };
    case "pending":
      return { input: { status: "pending_xxx" }, parsed };
    case "dash":
    case "empty":
      return { input: { status: "empty" }, parsed };
    case "text":
      return { input: { status: "not_measured", textValue: parsed.raw }, parsed };
  }
}

function norm(u: string): string {
  return u.toLocaleLowerCase("tr").replace(/³/g, "3").replace(/²/g, "2").replace(/\s+/g, "");
}

// ─── Lokal yeniden hesap etki kümesi (kural 5) ──────────────────────────────

/**
 * Bir metrik değişince hangi göstergelerin (kod) yeniden hesaplanacağı: aktif formülünde
 * doğrudan/dolaylı o koda referans verenler (+ konsolide/ara toplam kolonları her zaman).
 */
export function affectedCodes(
  indicators: KpiResolvedIndicator[],
  changedCode: string,
): Set<string> {
  const refsOf = new Map<string, string[]>();
  for (const i of indicators) {
    const active =
      i.formulas.find((f) => f.id === i.activeFormulaId) ?? i.formulas.find((f) => f.isActive);
    if (!active) continue;
    const refs = active.expression.match(/[a-z][a-z0-9_]*/g) ?? [];
    refsOf.set(i.code, refs);
  }
  const out = new Set<string>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const [code, refs] of refsOf) {
      if (out.has(code)) continue;
      if (refs.includes(changedCode) || refs.some((r) => out.has(r))) {
        out.add(code);
        grew = true;
      }
    }
  }
  return out;
}

// ─── Değişim kolonu (kural: showChange → son iki yıl) ────────────────────────

export function changeForIndicator(
  ind: KpiResolvedIndicator,
  columns: GridColumn[],
): { pct: number | null; years: [number, number] | null } {
  const yearCols = columns.filter((c) => c.kind === "year").sort((a, b) => b.year - a.year);
  if (yearCols.length < 2) return { pct: null, years: null };
  const [cur, prev] = [yearCols[0]!, yearCols[1]!];
  const a = readCell(ind, cur);
  const b = readCell(ind, prev);
  if (
    a?.status !== "filled" ||
    b?.status !== "filled" ||
    typeof a.value !== "number" ||
    typeof b.value !== "number"
  ) {
    return { pct: null, years: [cur.year, prev.year] };
  }
  return { pct: changePercent(a.value, b.value), years: [cur.year, prev.year] };
}

/** K27 — aktif yaprak işletmeler (grup düğümleri hariç), sıralı. */
export function leafEntities(grid: KpiResolvedGrid): KpiResolvedGrid["entities"] {
  return grid.entities
    .filter((e) => e.isActive && !grid.entities.some((c) => c.parentId === e.id && c.isActive))
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

// ─── Formül popover girdileri (KpiGrid.tsx'ten taşındı — ≤600 satır kuralı) ─────────────────

export type FormulaPopoverInputs = {
  code: string;
  label: string;
  cell: KpiResolvedCell | undefined;
  indicatorId: string;
}[];

/**
 * Aktif formülün girdileri (aynı kolon); `hidden` = grid'de olmayan referanslar (portalda `closed`
 * girdi — DR11); `labels` = kod → etiket (portal formül popover'ı ham kod göstermez — P3-18).
 * Girdi bir bantsa (çocuğu varsa) çocukları listelenir.
 */
export function formulaInputsFor(
  ind: KpiResolvedIndicator,
  grid: KpiResolvedGrid,
  col: GridColumn,
): { inputs: FormulaPopoverInputs; hidden: string[]; labels: Record<string, string> } {
  const active =
    ind.formulas.find((f) => f.id === ind.activeFormulaId) ?? ind.formulas.find((f) => f.isActive);
  const labels: Record<string, string> = {};
  if (!active) return { inputs: [], hidden: [], labels };
  const refs = Array.from(new Set(active.expression.match(/[a-z][a-z0-9_]*/g) ?? []));
  const out: FormulaPopoverInputs = [];
  const hidden: string[] = [];
  for (const code of refs) {
    const target = grid.indicators.find((i) => i.code === code);
    if (!target) {
      hidden.push(code);
      continue;
    }
    labels[code] = target.label;
    const kids = grid.indicators.filter((i) => i.parentId === target.id);
    for (const t of kids.length > 0 ? kids : [target]) {
      out.push({ code: t.code, label: t.label, cell: readCell(t, col), indicatorId: t.id });
    }
  }
  return { inputs: out, hidden, labels };
}
